import { milestoneMeta } from "./ui";
import type { MilestoneState } from "../types";

/**
 * „notatki · pliki" na kroku procesu — jeden komponent dla karty na tablicy kamieni
 * i dla kafelka na grafie procesu, żeby oba widoki pokazywały te same liczby (a nie,
 * jak wcześniej, tablica jedno, a graf nic). Liczby liczy backend na kamieniu.
 */
export function MilestoneCounts({
  notes,
  files,
  className,
}: {
  notes?: number | null;
  files?: number | null;
  className?: string;
}) {
  const n = notes ?? 0;
  const f = files ?? 0;
  const title =
    n || f
      ? `Notatki: ${n} · pliki: ${f}`
      : "Brak notatek i plików — dodasz je w szczegółach kroku";
  return (
    <span className={`inline-flex items-center gap-1.5 font-mono text-[10px] ${className ?? ""}`} title={title}>
      <span className={n ? "" : "opacity-50"}>💬 {n}</span>
      <span className={f ? "" : "opacity-50"}>📎 {f}</span>
    </span>
  );
}

/** Sygnały z grafu procesu doklejane do karty na tablicy kamieni: krok wisi na
 *  niedomkniętych poprzednikach / leży na ścieżce krytycznej. Bez tego ten sam
 *  kamień był „W trakcie" na kanbanie, a na schemacie blokowym świecił ⚠. */
export type MilestoneFlags = { violation?: boolean; missing?: string[]; critical?: boolean };

/** ⚠ / ◆ na karcie — te same znaki i te same znaczenia co na kafelku grafu. */
export function MilestoneFlagMarks({ flags }: { flags?: MilestoneFlags }) {
  if (!flags?.violation && !flags?.critical) return null;
  return (
    <span className="shrink-0 leading-none">
      {flags.violation && (
        <span
          className="font-bold text-[11px]"
          style={{ color: "#dc2626" }}
          title={`Krok jest w toku, ale wymaga wcześniej: ${(flags.missing ?? []).join(", ")}`}
        >
          ⚠
        </span>
      )}
      {flags.critical && (
        <span className="text-[11px]" style={{ color: "#ea580c" }} title="Ścieżka krytyczna">
          ◆
        </span>
      )}
    </span>
  );
}

/** Niebieska propozycja z silnika faktów przypięta do konkretnego kroku (ten sam
 *  sygnał, który na schemacie blokowym wisi w pasku „Wykryte fakty"). Karta pokazuje
 *  DOKĄD system chce przestawić krok, a powód siedzi w dymku — klik stosuje. */
export type MilestoneProposal = {
  node: string;
  label: string;
  current_state: string;
  proposed_state: string;
  reason: string;
};

/** Plakietka propozycji na karcie — w gramatyce znaczników ⚠/◆ powyżej. */
export function MilestoneProposalMark({
  proposal,
  onApply,
  disabled,
}: {
  proposal?: MilestoneProposal;
  onApply?: (p: MilestoneProposal) => void;
  disabled?: boolean;
}) {
  if (!proposal) return null;
  // Nazwa stanu z jednej mapy stanów (./ui); wersaliki robi klasa `uppercase`.
  const target = milestoneMeta(proposal.proposed_state as MilestoneState).label;
  return (
    <button
      type="button"
      // Karta (siatka) reaguje na klik, a na kanbanie siedzi na uchwycie drag&drop —
      // zatrzymujemy oba, żeby plakietka stosowała propozycję, a nie otwierała modal.
      onPointerDown={(e) => e.stopPropagation()}
      onClick={(e) => {
        e.stopPropagation();
        onApply?.(proposal);
      }}
      disabled={disabled || !onApply}
      title={`Propozycja z faktów: ${proposal.reason} — kliknij, aby zastosować`}
      className="shrink-0 border border-[#2563eb] px-1 font-mono text-[8px] font-bold uppercase leading-tight tracking-wide text-[#2563eb] disabled:opacity-50"
    >
      PROP.: {target}
    </button>
  );
}

/** Kroki „Wody Polskie" na tablicy — para sprzed modułu (warunki + pozwolenie) i
 *  wspólny krok w trybie podwykonawcy. Odznaka „do zlecenia" wisi na każdym z nich. */
export const WODY_MILESTONE_KEYS = ["wody_polskie_podwykonawca", "wp_pozwolenie", "wp_warunki"];

/** Ile zdarzeń wodnoprawnych czeka na zlecenie — tylko dla kroków Wód Polskich.
 *  `undefined` (starszy backend) i 0 (już zlecone) dają brak odznaki. */
export function wodyPendingFor(mkey: string, count?: number | null): number | undefined {
  return WODY_MILESTONE_KEYS.includes(mkey) && count ? count : undefined;
}

/** „N DO ZLECENIA" na karcie kamienia Wód Polskich. Świeci niezależnie od tego, czy krok
 *  jest „nie rozpoczęty" czy „dotyczy" — gaśnie, gdy zdarzenia trafią do zamówienia albo
 *  postępowania, a w trybie „robimy my" (gdzie zamówienie nie powstaje) gdy ktoś zamknie
 *  krok; serwer zeruje wtedy licznik. */
export function MilestoneWodyPendingMark({ count }: { count?: number | null }) {
  if (!count) return null;
  return (
    <span
      className="shrink-0 border border-[#d97706] px-1 font-mono text-[8px] font-bold uppercase leading-tight tracking-wide text-[#d97706]"
      title={`Wody Polskie: ${count} zdarzeń czeka na zlecenie podwykonawcy (zakładka „Wody Polskie")`}
    >
      {count} do zlecenia
    </span>
  );
}


/** Czy pokazać na tym kroku ostrzeżenie o nieaktualnej detekcji. */
export function wodyStaleFor(mkey: string, stale?: boolean | null): boolean | undefined {
  return WODY_MILESTONE_KEYS.includes(mkey) && stale ? true : undefined;
}

/** „ANALIZA NIEAKTUALNA" na karcie kamienia Wód Polskich: detekcję policzono na starszej
 *  wersji trasy. Automat świadomie odpuszcza przeliczanie przy poprawkach rysunku, więc
 *  bez tego znaku o rozjeździe wiedziałaby tylko sama zakładka. */
export function MilestoneWodyStaleMark({ stale }: { stale?: boolean | null }) {
  if (!stale) return null;
  return (
    <span
      className="shrink-0 border border-[#d97706] px-1 font-mono text-[8px] font-bold uppercase leading-tight tracking-wide text-[#d97706]"
      title="Wody Polskie: detekcję policzono na starszej wersji trasy — przelicz w zakładce „Wody Polskie”"
    >
      analiza nieaktualna
    </span>
  );
}
